/**
 * 광고 성과에서 특정 상품 빼기 (상품 그룹)
 *
 * 공구·협업 상품처럼 광고와 무관하게 팔린 상품이 광고 성과에 섞이면 소재 판단이
 * 흐려진다. 그런 상품을 그룹으로 묶으면 구매 이벤트에 `product_segment` 값이 실려
 * 나가고, 고객은 메타에서 맞춤 전환 하나로 그 그룹을 성과 계산에서 뺀다.
 *
 * 개념이 어려운 기능이라 화면을 ①담기 → ②메타 설정 → ③전송 확인 3단계로 세웠다.
 * 특히 ③이 "진짜 되고 있다"를 눈으로 보여주는 자리다.
 * 그 아래 Mixpanel 활용 카드는 단계가 아니다 — 설정 없이 이미 자동으로 실리는
 * product_segment 프로퍼티의 활용법(breakdown/제외 필터/순매출) 안내이며,
 * Mixpanel 연동 샵에만 보인다.
 *
 * 데스크톱 전용 (편집 화면 — 모바일은 오버플로우만 보장).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertTriangle,
  BarChart3,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  Loader2,
  Plus,
  Tag,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { getGetShopQueryKey } from '@/api/generated/v2-shop/v2-shop'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Input } from '@/components/ui/Input'
import { Pagination } from '@/components/ui/Pagination'
import { ProductPickerModal } from '@/features/capi/ProductPickerModal'
import { productTagsApi } from '@/lib/api'
import { formatKstDate } from '@/lib/dateUtils'
import { useShop } from '@/lib/shopContext'

export const Route = createFileRoute('/_dashboard/product-groups')({
  component: ProductGroupsPage,
})

const PAGE_SIZE = 20

/** 서버와 동일한 그룹명 규칙 (백엔드 TAG_PATTERN 과 일치시킬 것) */
const TAG_PATTERN = /^[a-z0-9][a-z0-9_-]{0,31}$/

/** 고객이 메타 맞춤 전환 규칙에 입력할 매개변수 이름 (백엔드/트래커와 동일)
 * Meta 표준 content_type 값 'product_group'과의 혼동을 피해 product_segment 로 명명 */
const PARAMETER_NAME = 'product_segment'

function errorMessage(error: unknown, fallback: string): string {
  // v2 AppError 핸들러는 {code, message} 를 탑레벨로 반환한다 (v2/exceptions.py).
  // FastAPI 기본 HTTPException 은 {detail: ...} 이라 둘 다 본다.
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data
  if (typeof data?.message === 'string') return data.message
  if (typeof data?.detail === 'string') return data.detail
  return fallback
}

/** 복사 버튼이 달린 값 — 메타 화면에 그대로 붙여넣게 한다 */
function CopyValue({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('복사하지 못했어요. 직접 입력해주세요')
    }
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-on-surface-variant w-16 shrink-0">
        {label}
      </span>
      <code className="flex-1 max-w-sm px-2 py-1 rounded bg-surface-variant/60 text-sm text-on-surface font-mono truncate">
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        className="p-1.5 text-outline hover:text-primary transition-colors"
        aria-label={`${label} 복사`}
      >
        {copied ? (
          <Check className="w-4 h-4 text-primary" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </button>
    </div>
  )
}

/** 단계 헤더 — 번호 + 제목 + 완료 표시 */
function StepHeader({
  step,
  title,
  done,
  children,
}: {
  step: number
  title: string
  done?: boolean
  children?: React.ReactNode
}) {
  return (
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="text-base flex items-center gap-2">
        <span
          className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
            done
              ? 'bg-primary text-on-primary'
              : 'bg-surface-variant text-on-surface-variant'
          }`}
        >
          {done ? <Check className="w-3.5 h-3.5" /> : step}
        </span>
        {title}
      </CardTitle>
      {children}
    </CardHeader>
  )
}

function ProductGroupsPage() {
  const queryClient = useQueryClient()
  const { shop } = useShop()
  // Mixpanel 미연동 샵에는 활용 카드를 숨긴다 — 열어볼 수 없는 안내는 소음이다
  const hasMixpanel = !!shop?.mixpanel_token
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const [offset, setOffset] = useState(0)
  const [creating, setCreating] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)

  // ── 그룹 목록 ────────────────────────────────────────────────
  const { data: groupsData, isLoading: loadingGroups } = useQuery({
    queryKey: ['product-tag-groups'],
    queryFn: productTagsApi.listGroups,
  })
  const groups = useMemo(() => groupsData?.groups ?? [], [groupsData])

  // 첫 그룹 자동 선택 / 삭제된 그룹 선택 해제
  useEffect(() => {
    // 새 그룹을 만드는 중에는 아직 서버에 없는 그룹이 선택돼 있다 —
    // 목록이 다시 조회돼도(포커스 refetch 등) 선택을 빼앗지 않는다
    if (creating) return
    if (!groups.length) {
      setSelectedTag(null)
      return
    }
    setSelectedTag((prev) =>
      prev && groups.some((g) => g.tag === prev) ? prev : groups[0].tag,
    )
  }, [groups, creating])

  // 아직 상품이 없는 새 그룹은 서버에 존재하지 않는다
  const isPendingGroup =
    !!selectedTag && !groups.some((g) => g.tag === selectedTag)

  // ── 담긴 상품 ───────────────────────────────────────────────
  const {
    data: productsData,
    isLoading: loadingProducts,
    isError: productsError,
  } = useQuery({
    queryKey: ['product-tag-products', selectedTag, offset],
    queryFn: () =>
      productTagsApi.listGroupProducts(
        selectedTag as string,
        offset,
        PAGE_SIZE,
      ),
    enabled: !!selectedTag && !isPendingGroup,
  })
  const products = productsData?.products ?? []
  const totalProducts = productsData?.total ?? 0

  // ── 전송 확인 ───────────────────────────────────────────────
  const {
    data: delivery,
    isLoading: loadingDelivery,
    isError: deliveryError,
  } = useQuery({
    queryKey: ['product-tag-delivery', selectedTag],
    queryFn: () => productTagsApi.getDelivery(selectedTag as string),
    enabled: !!selectedTag && !isPendingGroup,
  })

  const invalidate = async () => {
    // 사이드바 '상품 그룹' 노출 조건이 shop 응답(has_measurement_groups)에 있어서
    // 첫 그룹 생성/마지막 그룹 삭제 후 메뉴가 바로 반영되려면 함께 무효화해야 한다
    queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
    queryClient.invalidateQueries({ queryKey: ['product-tag-products'] })
    queryClient.invalidateQueries({ queryKey: ['product-tag-delivery'] })
    await queryClient.invalidateQueries({ queryKey: ['product-tag-groups'] })
  }

  // ── 쓰기 ────────────────────────────────────────────────────
  const addProducts = useMutation({
    mutationFn: ({ tag, productNos }: { tag: string; productNos: number[] }) =>
      productTagsApi.addProducts(tag, productNos),
    onSuccess: async (result, variables) => {
      setPickerOpen(false)
      // 목록 갱신을 기다린 뒤 선택을 확정한다 — stale 목록으로 이펙트가 돌면
      // 방금 만든 그룹이 목록에 없어서 다른 그룹으로 튄다
      await invalidate()
      setSelectedTag(variables.tag)
      setOffset(0)
      setCreating(false)
      toast.success(
        result.affected === 0
          ? '이미 담겨 있는 상품이에요'
          : `${result.affected}개 상품을 담았어요. 브라우저에서 발생하는 구매에 반영되기까지 최대 ` +
              `${Math.round(result.propagation_delay_seconds / 60)}분 걸려요`,
      )
    },
    onError: (error) =>
      toast.error(errorMessage(error, '상품을 담지 못했어요')),
  })

  const removeProducts = useMutation({
    mutationFn: ({ tag, productNos }: { tag: string; productNos?: number[] }) =>
      productTagsApi.removeProducts(tag, productNos),
    onSuccess: (result, variables) => {
      // 그룹 전체 삭제면 선택부터 푼다 — 안 그러면 사라진 그룹으로 재조회해
      // 404 가 나고 "담긴 상품은 그대로 있어요" 라는 반대 안내가 뜬다
      if (!variables.productNos) {
        setSelectedTag(null)
      }
      void invalidate()
      setOffset(0)
      if (result.affected === 0) {
        toast('삭제할 상품이 없었어요')
        return
      }
      toast.success(
        variables.productNos ? '그룹에서 뺐어요' : '그룹을 삭제했어요',
      )
    },
    onError: (error) =>
      toast.error(errorMessage(error, '상품을 빼지 못했어요')),
  })

  const handleCreateGroup = () => {
    const tag = newTag.trim()
    if (!TAG_PATTERN.test(tag)) {
      toast.error(
        '그룹 이름은 영문 소문자·숫자로 시작하고 영문 소문자, 숫자, - , _ 만 쓸 수 있어요',
      )
      return
    }
    if (groups.some((g) => g.tag === tag)) {
      toast.error('이미 있는 그룹이에요. 왼쪽 목록에서 선택해주세요')
      return
    }
    if (groups.some((g) => g.tag.includes(tag) || tag.includes(g.tag))) {
      toast.error(
        '기존 그룹과 이름이 서로 포함 관계예요. 메타가 두 그룹을 같이 잡아버려요',
      )
      return
    }
    // 그룹은 상품이 담겨야 서버에 생긴다 — 선택만 해두고 상품 담기로 넘어간다
    setSelectedTag(tag)
    setOffset(0)
    setNewTag('')
    setPickerOpen(true)
  }

  const handleDeleteGroup = (tag: string) => {
    if (
      !window.confirm(
        `'${tag}' 그룹을 삭제할까요?\n\n` +
          '메타에 만들어둔 맞춤 전환은 그대로 남고, 앞으로의 구매 이벤트에만 ' +
          '이 그룹 값이 실리지 않아요.',
      )
    ) {
      return
    }
    removeProducts.mutate({ tag })
  }

  const hasProducts = !isPendingGroup && totalProducts > 0
  const isDelivering = (delivery?.tagged_purchases ?? 0) > 0

  return (
    // 폭은 형제 페이지(이벤트 현황/홈)와 동일하게 5xl 로 제한 — 제한이 없으면
    // 와이드 모니터에서 복사 값 행이 화면 끝까지 늘어나 읽기 불편하다
    <div className="max-w-5xl mx-auto space-y-6">
      {/* 헤더 */}
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">
          광고 성과에서 특정 상품 빼기
        </h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          공구나 인플루언서 협업처럼 광고와 무관하게 팔린 상품이 광고 성과에
          섞이면 소재 판단이 흐려져요. 그런 상품을 그룹으로 묶어두면 메타에서 그
          그룹만 뺀 성과를 따로 볼 수 있어요.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* 그룹 목록 */}
        <Card className="h-fit">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">그룹</CardTitle>
            {!creating && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCreating(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                추가
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-2">
            {creating && (
              <div className="space-y-2 pb-2">
                <Input
                  autoFocus
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value.toLowerCase())}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
                  placeholder="예: gonggu"
                />
                <p className="text-xs text-on-surface-variant">
                  영문 소문자·숫자·- ·_ 만 사용해요. 이 이름을 메타에 그대로
                  입력하게 되니 알아보기 쉬운 이름으로요.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreateGroup}>
                    다음
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setCreating(false)
                      setNewTag('')
                    }}
                  >
                    취소
                  </Button>
                </div>
              </div>
            )}

            {loadingGroups ? (
              <div className="flex justify-center py-6 text-on-surface-variant">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : groups.length === 0 && !creating ? (
              <p className="text-sm text-on-surface-variant py-4">
                아직 그룹이 없어요. 추가를 눌러 만들어보세요.
              </p>
            ) : (
              groups.map((group) => (
                <button
                  type="button"
                  key={group.tag}
                  onClick={() => {
                    setSelectedTag(group.tag)
                    setOffset(0)
                  }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                    selectedTag === group.tag
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-surface-variant/50 text-on-surface'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Tag className="w-4 h-4 shrink-0" />
                    <span className="font-mono text-sm truncate">
                      {group.tag}
                    </span>
                  </span>
                  <Badge variant="secondary">{group.product_count}</Badge>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        {/* 3단계 */}
        {!selectedTag ? (
          <Card>
            <CardContent className="py-10">
              <EmptyState
                icon={<Tag className="h-12 w-12" />}
                title="그룹을 만들어주세요"
                description="광고 성과에서 빼고 싶은 상품들을 하나의 그룹으로 묶는 것부터 시작해요"
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* ① 상품 담기 */}
            <Card>
              <StepHeader step={1} title="뺄 상품 담기" done={hasProducts}>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setPickerOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    상품 담기
                  </Button>
                  {!isPendingGroup && (
                    <Button
                      size="sm"
                      variant="danger-outline"
                      onClick={() => handleDeleteGroup(selectedTag)}
                      aria-label="그룹 삭제"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </StepHeader>
              <CardContent>
                {isPendingGroup ? (
                  <p className="text-sm text-on-surface-variant py-2">
                    <span className="font-mono text-primary">
                      {selectedTag}
                    </span>{' '}
                    그룹은 상품을 담아야 저장돼요.
                  </p>
                ) : loadingProducts ? (
                  <div className="flex justify-center py-8 text-on-surface-variant">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                ) : productsError ? (
                  // 조회 실패를 "없음"으로 표시하면 태그가 날아간 줄 안다
                  <div className="flex items-start gap-2 py-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-error shrink-0 mt-0.5" />
                    <div>
                      <p className="text-on-surface">
                        상품 정보를 불러오지 못했어요 (카페24 응답 지연일 수
                        있어요).
                      </p>
                      <p className="text-on-surface-variant">
                        담긴 상품은 그대로 있어요. 잠시 후 다시 시도해주세요.
                      </p>
                    </div>
                  </div>
                ) : products.length === 0 ? (
                  <p className="text-sm text-on-surface-variant py-2">
                    담긴 상품이 없어요.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div className="divide-y divide-outline-variant border border-outline-variant rounded-lg overflow-x-auto">
                      {products.map((product) => (
                        <div
                          key={product.product_no}
                          className="flex items-center gap-3 px-4 py-3"
                        >
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt=""
                              className="w-10 h-10 rounded object-cover bg-surface-variant shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-surface-variant shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-on-surface truncate">
                              {product.product_name ?? (
                                <span className="text-on-surface-variant">
                                  (상품 정보 없음)
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-on-surface-variant flex items-center gap-1.5">
                              #{product.product_no}
                              {product.selling === false && ' · 판매중지'}
                              {product.missing && (
                                <span className="flex items-center gap-1 text-error">
                                  <AlertTriangle className="w-3 h-3" />
                                  카페24에서 삭제된 상품
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              removeProducts.mutate({
                                tag: selectedTag,
                                productNos: [product.product_no],
                              })
                            }
                            className="p-2 text-outline hover:text-error transition-colors"
                            aria-label="그룹에서 빼기"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {totalProducts > PAGE_SIZE && (
                      <Pagination
                        page={Math.floor(offset / PAGE_SIZE) + 1}
                        totalPages={Math.ceil(totalProducts / PAGE_SIZE)}
                        onPageChange={(page) =>
                          setOffset((page - 1) * PAGE_SIZE)
                        }
                      />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ② 메타에서 맞춤 전환 만들기 */}
            <Card>
              <StepHeader step={2} title="메타에서 맞춤 전환 만들기" />
              <CardContent className="space-y-3 text-sm text-on-surface-variant">
                <p>
                  이벤트 관리자 → <strong>맞춤 전환</strong> → 만들기에서 데이터
                  소스는 연동한 픽셀, 이벤트는 <strong>구매</strong>를 고르고
                  규칙을 아래처럼 넣어요.
                </p>
                <div className="space-y-2 p-3 rounded-lg border border-outline-variant">
                  <CopyValue label="규칙" value="Event Parameters" />
                  <CopyValue label="매개변수" value={PARAMETER_NAME} />
                  <CopyValue label="조건" value="포함 안 함" />
                  <CopyValue label="값" value={selectedTag} />
                </div>
                <p>
                  이 맞춤 전환으로 광고 보고서를 보면{' '}
                  <span className="font-mono text-primary">{selectedTag}</span>{' '}
                  그룹의 구매가 빠진 성과가 나와요. 기존 &lsquo;구매&rsquo;
                  지표는 그대로 남아서 언제든 비교할 수 있어요.
                </p>
                <p className="flex items-start gap-1.5">
                  <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    그룹에서 상품을 넣거나 빼면 브라우저에서 발생하는 구매에
                    반영되기까지 최대 1시간 걸려요 (서버에서 보내는 구매는 즉시
                    반영).
                  </span>
                </p>
              </CardContent>
            </Card>

            {/* ③ 전송 확인 */}
            <Card>
              <StepHeader
                step={3}
                title="잘 전송되는지 확인"
                done={isDelivering}
              />
              <CardContent>
                {isPendingGroup ? (
                  <p className="text-sm text-on-surface-variant">
                    상품을 담으면 여기서 전송 여부를 확인할 수 있어요.
                  </p>
                ) : loadingDelivery ? (
                  <div className="flex justify-center py-4 text-on-surface-variant">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                ) : deliveryError || !delivery ? (
                  <div className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-error shrink-0 mt-0.5" />
                    <p className="text-on-surface-variant">
                      전송 상태를 확인하지 못했어요. 잠시 후 다시 시도해주세요.
                    </p>
                  </div>
                ) : isDelivering ? (
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-on-surface">
                        최근 {delivery.days}일 구매 {delivery.total_purchases}건
                        중{' '}
                        <strong className="text-primary">
                          {delivery.tagged_purchases}건
                        </strong>
                        에 이 그룹이 실려 나갔어요.
                      </p>
                      <p className="text-on-surface-variant mt-0.5">
                        마지막 전송{' '}
                        {delivery.last_sent_at
                          ? formatKstDate(delivery.last_sent_at)
                          : '-'}{' '}
                        · 서버에서 보낸 구매 기준이에요
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-on-surface-variant space-y-1">
                    <p className="text-on-surface">
                      아직 이 그룹이 실린 구매가 없어요.
                    </p>
                    <p>
                      {delivery.total_purchases === 0
                        ? `최근 ${delivery.days}일간 구매 자체가 없어요. 주문이 들어오면 여기에 표시돼요.`
                        : `최근 ${delivery.days}일 구매 ${delivery.total_purchases}건 중에는 이 그룹 상품이 없었어요. 담은 상품이 팔리면 표시돼요.`}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Mixpanel 활용 안내 — ①~③과 달리 "해야 하는 설정"이 아니라
                이미 자동으로 되고 있는 활용처라 단계 번호를 붙이지 않는다 */}
            {hasMixpanel && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Mixpanel에서도 바로 볼 수 있어요
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-on-surface-variant">
                  <p>
                    이 그룹 값은 별도 설정 없이 Mixpanel의{' '}
                    <strong>purchase · cancel_order · refund</strong> 이벤트에{' '}
                    <span className="font-mono">{PARAMETER_NAME}</span>{' '}
                    프로퍼티로 자동으로 실려요. 서버에서 보내는 값이라 저장 즉시
                    다음 주문부터 반영돼요 (위의 1시간 지연은 메타 픽셀
                    얘기예요).
                  </p>
                  <div className="space-y-2 p-3 rounded-lg border border-outline-variant">
                    <CopyValue label="프로퍼티" value={PARAMETER_NAME} />
                  </div>
                  <ul className="space-y-1.5 list-disc pl-4">
                    <li>
                      <strong className="text-on-surface">그룹별 매출</strong> —
                      보고서에서 purchase 이벤트를{' '}
                      <span className="font-mono">{PARAMETER_NAME}</span>로
                      breakdown 하면 그룹별로 나뉘어 보여요.
                    </li>
                    <li>
                      <strong className="text-on-surface">
                        그룹 제외 분석
                      </strong>{' '}
                      — 필터를 &ldquo;
                      <span className="font-mono">{PARAMETER_NAME}</span> 에{' '}
                      <span className="font-mono text-primary">
                        {selectedTag}
                      </span>{' '}
                      포함 안 함&rdquo;으로 걸면 이 그룹을 뺀 매출·퍼널·코호트를
                      볼 수 있어요.
                    </li>
                    <li>
                      <strong className="text-on-surface">
                        그룹 제외 순매출
                      </strong>{' '}
                      — 같은 필터를 purchase / cancel_order / refund 세 이벤트에
                      걸고 차감하면 취소·환불까지 반영된 순매출이 나와요.
                    </li>
                  </ul>
                  <p className="flex items-start gap-1.5">
                    <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      태그 변경은 다음 주문부터 반영되고, 이미 쌓인 과거
                      이벤트에는 소급되지 않아요. 태그 상품이 없는 주문에는{' '}
                      <span className="font-mono">default</span> 값이 실려요.
                    </span>
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {selectedTag && (
        <ProductPickerModal
          open={pickerOpen}
          onOpenChange={(open) => {
            setPickerOpen(open)
            // 새 그룹인데 상품을 안 담고 닫으면 그룹은 생기지 않는다
            if (!open && isPendingGroup) {
              setCreating(false)
              setSelectedTag(groups[0]?.tag ?? null)
            }
          }}
          tag={selectedTag}
          existingProductNos={productsData?.all_product_nos ?? []}
          onConfirm={(productNos) =>
            addProducts.mutate({ tag: selectedTag, productNos })
          }
          saving={addProducts.isPending}
        />
      )}
    </div>
  )
}
